import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listTasks, updateTaskStatus, type TaskItem, type Page } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  BACKLOG: "bg-gray-700 text-gray-300",
  TODO: "bg-blue-900/50 text-blue-300",
  IN_PROGRESS: "bg-yellow-900/50 text-yellow-300",
  IN_REVIEW: "bg-purple-900/50 text-purple-300",
  DONE: "bg-green-900/50 text-green-300",
  ARCHIVED: "bg-gray-800 text-gray-500",
};

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: "text-red-400",
  HIGH: "text-orange-400",
  MEDIUM: "text-yellow-400",
  LOW: "text-gray-400",
};

const STATUS_TRANSITIONS: Record<string, string | null> = {
  BACKLOG: "TODO",
  TODO: "IN_PROGRESS",
  IN_PROGRESS: "IN_REVIEW",
  IN_REVIEW: "DONE",
  DONE: null,
};

export function DeliveryTasksPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<Page<TaskItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") ?? "");
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listTasks(token, { type: typeFilter || undefined, status: statusFilter || undefined, limit: 100 })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, typeFilter, statusFilter]);

  const advance = async (task: TaskItem) => {
    const next = STATUS_TRANSITIONS[task.status];
    if (!next || token == null) return;
    try {
      setActing(task.id);
      await updateTaskStatus(token, task.id, task.version, next);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Tasks</h1>

        <div className="flex gap-3 flex-wrap">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Types</option>
            {["EPIC", "FEATURE", "TASK", "SUBTASK", "BUG", "SPIKE"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).filter(s => s !== "ARCHIVED").map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        {data != null && (
          <div className="space-y-2">
            <p className="text-gray-400 text-sm">{data.total} tasks</p>
            {data.items.map((task) => (
              <div key={task.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">{task.type}</span>
                      <span className={`text-xs font-medium ${PRIORITY_COLORS[task.priority] ?? "text-gray-400"}`}>{task.priority}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[task.status] ?? "bg-gray-700 text-gray-300"}`}>{task.status}</span>
                      {task.storyPoints != null && <span className="text-xs text-gray-500">{task.storyPoints}pt</span>}
                    </div>
                    <p className="text-white font-medium mt-1">{task.title}</p>
                    {task.description && <p className="text-gray-400 text-sm mt-1 line-clamp-2">{task.description}</p>}
                    {task.dueDate && <p className="text-xs text-gray-500 mt-1">Due: {task.dueDate.split("T")[0]}</p>}
                    {task.labels.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {task.labels.map(l => <span key={l} className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">{l}</span>)}
                      </div>
                    )}
                  </div>
                  {STATUS_TRANSITIONS[task.status] != null && (
                    <button disabled={acting != null} onClick={() => advance(task)} className="px-3 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50 shrink-0">
                      → {STATUS_TRANSITIONS[task.status]}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {data.items.length === 0 && <p className="text-gray-400 text-sm">No tasks found.</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
